import React from 'react';
import PropTypes from 'prop-types';
import { Divider, Typography, ListItem, ButtonBase, List, CircularProgress } from '@material-ui/core';

import MoreMenuItem from 'modules/shell/components/MoreMenu/MoreMenuItem';
import Checkbox from 'sharedComponents/Checkbox';
import cx from 'classnames';

import BackToTop from '../BackToTop';
import { getItemsListWithAlphaKey, getAlphabetNavigationMap } from '../../utils/designersNavigation';

const AutoSizer = React.lazy(() => import('react-virtualized/dist/es/AutoSizer'));
const InfiniteLoader = React.lazy(() => import('react-virtualized/dist/es/InfiniteLoader'));
const ListVirtualized = React.lazy(() => import('react-virtualized/dist/es/List'));

const STATUS_LOADING = 1;
const STATUS_LOADED = 2;

export const FIRST_FETCH_SIZE = 30;
export const FETCH_ALL_SIZE = -1;
const THRESHOLD_SIZE = 50;

const DesignerNavigationInfiniteScroll = ({
  classes,
  fetch,
  searchPage,
  history,
  data,
  searchValue,
  loading,
  match,
  checkboxMode,
  checkboxClickHandler,
  checkedItems,
  isSystemGenerated,
}) => {
  const [filteredResults, setFilteredResults] = React.useState(null);
  const [alphaNavigationMap, setAlphaNavigationMap] = React.useState(null);
  const listRef = React.useRef(null);
  const loadedRowsMap = React.useRef({});
  const scrollToRow = React.useRef(null);
  const [isSearchReady, setSearchReady] = React.useState(false);
  const isValidSearch = searchPage && searchValue.trim('').length > 0;
  const [systemGenerated, setSystemGenerated] = React.useState(isSystemGenerated);

  React.useEffect(() => {
    if (!searchPage) {
      fetch({ from: 0, size: FIRST_FETCH_SIZE }).then(() => {
        for (let i = 0; i < FIRST_FETCH_SIZE; i++) {
          loadedRowsMap.current[i] = STATUS_LOADED;
        }
      });
    }
  }, [searchPage]);

  React.useEffect(() => {
    if (searchPage) {
      loadedRowsMap.current = {};
      setFilteredResults(null);
      setAlphaNavigationMap(null);
      return;
    }

    if (data && data.children && data.alphabet) {
      const alphabetMap = getAlphabetNavigationMap(data.alphabet);
      setAlphaNavigationMap(alphabetMap);
      setFilteredResults(getItemsListWithAlphaKey(data.children, alphabetMap));
    }
  }, [searchPage, data]);

  React.useEffect(() => {
    if (isValidSearch) {
      setSearchReady(false);
      fetch({
        from: 0,
        size: FETCH_ALL_SIZE,
        searchValue: searchValue.trim(''),
      }).then(() => {
        setSearchReady(true);
      });
    }
  }, [searchValue, isValidSearch]);

  const scroll = (position) => {
    listRef.current.scrollToRow(position + 1);

    // Double scroll is needed because of dynamic height.
    // More details about the issue and workarounds: https://github.com/bvaughn/react-virtualized/issues/995
    new Promise((resolve) => {
      scrollToRow.current = { position, resolve };
    }).then(() => {
      listRef.current.scrollToRow(position + 1);
    });
  };

  const rowsRendered = async () => {
    if (scrollToRow.current) {
      const { position, resolve } = scrollToRow.current;

      if (loadedRowsMap.current[position] === STATUS_LOADED) {
        await resolve();
        scrollToRow.current = null;
      }
    }
  };

  const menuClickHandler = ({ id, isLeafNode, showProductsCatId, showCategoryCatId }) => {
    const currentPageId = match.params.id;
    if (showProductsCatId || isLeafNode) {
      const navToPlpId = showProductsCatId || id;
      history.push({
        pathname: `/products/listing/${navToPlpId}`,
        search: `?memoryParentId=${currentPageId}`,
      });
    } else {
      const navToCategoryId = showCategoryCatId || id;
      history.push({
        pathname: `/products/catalog/browse/${navToCategoryId}`,
        search: `?memoryParentId=${currentPageId}`,
      });
    }
  };

  const loadMoreRows = ({ startIndex, stopIndex }) => {
    const items = Object.values(data.children);
    let allRowsAreAlreadyLoaded = true;

    for (let i = startIndex; i <= stopIndex; i++) {
      if (items[i]) {
        loadedRowsMap.current[i] = STATUS_LOADED;
      } else {
        loadedRowsMap.current[i] = STATUS_LOADING;
        allRowsAreAlreadyLoaded = false;
      }
    }

    if (allRowsAreAlreadyLoaded) {
      return Promise.resolve();
    }

    const from = startIndex;
    const size = stopIndex >= data.childrenCount ? data.childrenCount - startIndex : stopIndex - startIndex + 1;

    return fetch({ from, size }).then(() => {
      for (let i = startIndex; i <= stopIndex; i++) {
        loadedRowsMap.current[i] = STATUS_LOADED;
      }
    });
  };

  const isRowLoaded = ({ index }) => {
    return !!loadedRowsMap.current[index];
  };

  const rowRenderer = ({ index, key, style }) => {
    if (index === 0) {
      const navigationArray = Object.keys(alphaNavigationMap);

      const renderLetter = (letter) => {
        const position = alphaNavigationMap[letter];
        const disabled = position === -1;

        return (
          <ButtonBase
            key={letter}
            className={classes.listItemText}
            onClick={() => scroll(position)}
            disabled={disabled}
          >
            <Typography color="primary" className={disabled ? classes.disabled : classes.alphaIndexTitle} variant="h3">
              {letter}
            </Typography>
          </ButtonBase>
        );
      };

      return (
        <List id="alphaList" className={classes.list} style={style} key={key}>
          <ListItem className={classes.listItem}>{navigationArray.map(renderLetter)}</ListItem>
        </List>
      );
    }

    const handleChange = (label) => {
      checkboxClickHandler(label);
      setSystemGenerated(false);
    };

    const item = filteredResults[index - 1];
    const designerRowItem = checkboxMode ? (
      <Checkbox
        key={item?.label}
        checked={checkedItems.includes(item?.label)}
        name={item?.label}
        onChange={() => handleChange(item.label)}
        label={item?.label}
        classes={{ root: systemGenerated ? classes.checkbox : classes.saChecked, labelRoot: classes.labelRoot }}
      />
    ) : (
      <MoreMenuItem
        title={item?.label}
        data-testid={`designer-catalog-menu-${index}-link`}
        showCircle={false}
        classMenu={classes.catalogMenuText}
        onClick={() =>
          menuClickHandler({
            id: item.id,
            isLeadNode: item.isLeafNode,
            showProductsCatId: item.showProductsCatId,
            showCategoryCatId: item.showCategoryCatId,
          })
        }
      />
    );

    if (item && item.alphaKey) {
      return (
        <div key={key} className={classes.container} style={style}>
          <Typography variant="h4" className={classes.title}>
            {item.alphaKey}
          </Typography>
          <Divider variant="middle" className={classes.divider} />
          {designerRowItem}
        </div>
      );
    }

    return (
      <div key={key} style={style} className={cx(checkboxMode && classes.designerSecondaryItem)}>
        {designerRowItem}
      </div>
    );
  };

  const rowHeight = ({ index }) => {
    if (index === 0) {
      return 224;
    }

    if (alphaNavigationMap && Object.values(alphaNavigationMap).includes(index - 1)) {
      return 102;
    }

    return 48;
  };

  const renderSearchResults = (items) => (
    <div className={classes.container}>
      {items.map(({ label, id, isLeafNode, showProductsCatId, showCategoryCatId }, index) => (
        <MoreMenuItem
          key={id}
          title={label}
          onClick={() => menuClickHandler({ id, isLeafNode, showProductsCatId, showCategoryCatId })}
          data-testid={`designer-catalog-menu-${index}-link`}
          showCircle={false}
          classMenu={classes.catalogMenuText}
        />
      ))}
    </div>
  );

  return (
    <>
      {loading && <CircularProgress className={classes.loader} data-testid="circular-progress" />}
      {!searchPage && alphaNavigationMap && (
        <BackToTop
          classes={{ backToTop: classes.backToTop, arrowIcon: classes.backToTopIcon }}
          onClick={() => scroll(-1)}
        >
          <InfiniteLoader
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={data.childrenCount + 1}
            threshold={THRESHOLD_SIZE}
          >
            {({ onRowsRendered, registerChild }) => (
              <div className={classes.autoSizerContainer}>
                <AutoSizer>
                  {({ width, height }) => (
                    <ListVirtualized
                      scrollToAlignment="start"
                      ref={(list) => {
                        registerChild(list);
                        listRef.current = list;
                      }}
                      className={classes.virtualizedListNoAlphabet}
                      height={height}
                      onRowsRendered={(...arg) => {
                        onRowsRendered(...arg);
                        rowsRendered(...arg);
                      }}
                      rowCount={data.childrenCount + 1}
                      rowHeight={rowHeight}
                      rowRenderer={rowRenderer}
                      width={width}
                    />
                  )}
                </AutoSizer>
              </div>
            )}
          </InfiniteLoader>
        </BackToTop>
      )}
      {isValidSearch && isSearchReady && renderSearchResults(data.children)}
      {isValidSearch && isSearchReady && data.children.length === 0 && (
        <Typography className={classes.noResults} variant="h2">
          No Results
        </Typography>
      )}
    </>
  );
};

DesignerNavigationInfiniteScroll.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  fetch: PropTypes.func.isRequired,
  history: PropTypes.shape({}),
  loading: PropTypes.bool.isRequired,
  searchPage: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  checkedItems: PropTypes.arrayOf(PropTypes.string),
  checkboxClickHandler: PropTypes.func,
  checkboxMode: PropTypes.bool,
  isSystemGenerated: PropTypes.bool,
};

DesignerNavigationInfiniteScroll.defaultProps = {
  checkedItems: [],
  isSystemGenerated: false,
};

export default DesignerNavigationInfiniteScroll;
